import React, { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../../../context/AuthProvider'
import { api } from '../../../lib/api'
import { Button } from '../../../components/ui/button'
import { CheckCircle2, XCircle, Loader2, CreditCard, AlertCircle, Pencil } from 'lucide-react'

export default function ParishBank() {
  const { user, loading: authLoading } = useAuth()
  const [loading, setLoading] = useState(true)
  const [onboardLoading, setOnboardLoading] = useState(false)
  const [editLoading, setEditLoading] = useState(false)
  const [payoutLoading, setPayoutLoading] = useState(false)
  const [parishId, setParishId] = useState(null)
  const [status, setStatus] = useState(null)
  const [error, setError] = useState(null)

  const fetchParishId = useCallback(async () => {
    if (!user?.id) return null
    try {
      const userData = await api.getMe()
      const roleDetails = userData?.roleDetails || []
      const parishAdminRole = roleDetails.find((r) => r.name === 'parish-admin')
      return parishAdminRole?.parishId ?? null
    } catch {
      return null
    }
  }, [user?.id])

  const fetchStatus = useCallback(async (id) => {
    if (!id) return
    try {
      setError(null)
      const data = await api.stripeStatus(id)
      setStatus(data)
    } catch (err) {
      setError(err.message || 'Failed to load Stripe status')
    }
  }, [])

  useEffect(() => {
    if (user?.id) {
      setLoading(true)
      fetchParishId().then((id) => {
        setParishId(id)
        if (id) {
          fetchStatus(id).finally(() => setLoading(false))
        } else {
          setLoading(false)
        }
      })
    }
  }, [user?.id, fetchParishId, fetchStatus])

  // Refetch when returning from Stripe (e.g. tab focus) if not fully onboarded
  useEffect(() => {
    if (!parishId || !status?.isConnected) return
    const fullyOnboarded = status.chargesEnabled && status.payoutsEnabled
    if (fullyOnboarded) return

    const onFocus = () => fetchStatus(parishId)
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [parishId, status?.isConnected, status?.chargesEnabled, status?.payoutsEnabled, fetchStatus])

  const handleStartOnboarding = async () => {
    if (!parishId) return
    setOnboardLoading(true)
    setError(null)
    const bankUrl = `${window.location.origin}/app/parish/bank`
    try {
      const { url } = await api.stripeOnboard(parishId, {
        returnUrl: bankUrl,
        refreshUrl: bankUrl,
      })
      window.location.href = url
    } catch (err) {
      setError(err.message || 'Failed to start Stripe setup')
      setOnboardLoading(false)
    }
  }

  const handleEditDetails = async () => {
    if (!parishId) return
    setEditLoading(true)
    setError(null)
    try {
      const { url } = await api.stripeUpdateLink(parishId)
      window.location.href = url
    } catch (err) {
      setError(err.message || 'Failed to open Stripe')
      setEditLoading(false)
    }
  }

  const handleInstantPayout = async () => {
    if (!parishId || !status?.balances?.available) return
    setPayoutLoading(true)
    setError(null)
    try {
      await api.stripeInstantPayout(parishId, status.balances.available)
      await fetchStatus(parishId)
    } catch (err) {
      setError(err.message || 'Failed to trigger instant payout')
    } finally {
      setPayoutLoading(false)
    }
  }

  const fullyOnboarded = status?.isConnected && status?.chargesEnabled && status?.payoutsEnabled
  const needsOnboarding = !status?.isConnected || !fullyOnboarded
  const canStartOrContinue = needsOnboarding && !onboardLoading

  if (authLoading) {
    return (
      <div>
        <h1 className="text-3xl font-bold font-display text-charcoal mb-6">Bank Account</h1>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 text-gold animate-spin" />
        </div>
      </div>
    )
  }

  if (loading && !parishId) {
    return (
      <div>
        <h1 className="text-3xl font-bold font-display text-charcoal mb-6">Bank Account</h1>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 text-gold animate-spin" />
        </div>
      </div>
    )
  }

  if (!parishId) {
    return (
      <div>
        <h1 className="text-3xl font-bold font-display text-charcoal mb-6">Bank Account</h1>
        <div className="bg-red-50 border border-red-200 rounded p-6">
          <p className="text-red-600">
            You are not associated with a parish as a parish admin. Please contact support.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div>
      <h1 className="text-3xl font-bold font-display text-charcoal mb-6">Bank Account</h1>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      )}

      <div className="bg-white border border-surface-border rounded p-6 space-y-6">
        <p className="text-charcoal-muted">
          Connect your parish to Stripe to receive donations. Complete a short verification with
          Stripe to enable payouts.
        </p>

        {loading && !status ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 text-gold animate-spin" />
          </div>
        ) : (
          <>
            {/* Status block */}
            <div>
              <h2 className="text-xl font-semibold font-display text-charcoal mb-2">
                Stripe Connect Status
              </h2>
              {!status?.isConnected ? (
                <p className="text-amber-400/90 font-medium">Not connected</p>
              ) : (
                <p
                  className={`text-lg font-semibold ${fullyOnboarded ? 'text-green-400' : 'text-amber-400/90'
                    }`}
                >
                  {fullyOnboarded ? 'Active' : 'Pending'}
                </p>
              )}
            </div>

            {status?.isConnected && (
              <div className="space-y-4 bg-surface-linen/50 rounded p-4 border border-surface-border">
                <div className="flex justify-between items-center py-2 border-b border-surface-border">
                  <span className="text-sm font-medium text-charcoal-muted">Payouts enabled</span>
                  {status.payoutsEnabled ? (
                    <CheckCircle2 className="h-5 w-5 text-green-400" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-600/80" />
                  )}
                </div>
                <div className="flex justify-between items-center py-2 border-b border-surface-border">
                  <span className="text-sm font-medium text-charcoal-muted">Charges enabled</span>
                  {status.chargesEnabled ? (
                    <CheckCircle2 className="h-5 w-5 text-green-400" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-600/80" />
                  )}
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-sm font-medium text-charcoal-muted">Details submitted</span>
                  {status.detailsSubmitted ? (
                    <CheckCircle2 className="h-5 w-5 text-green-400" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-600/80" />
                  )}
                </div>
              </div>
            )}

            {/* Action button */}
            {canStartOrContinue && (
              <Button
                onClick={handleStartOnboarding}
                disabled={onboardLoading}
                className="bg-gold text-black hover:bg-gold/90 flex items-center gap-2"
              >
                {onboardLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-4 h-4" />
                    {status?.isConnected ? 'Continue Stripe Setup' : 'Start Stripe Setup'}
                  </>
                )}
              </Button>
            )}

            {fullyOnboarded && (
              <div className="space-y-6 mt-6">
                <div className="bg-surface-linen/50 rounded p-4 border border-surface-border">
                  <h3 className="text-lg font-semibold font-display text-charcoal mb-4">Account Balance & Schedule</h3>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="bg-white p-4 rounded border border-surface-border">
                      <p className="text-sm text-charcoal-muted">Available Volume</p>
                      <p className="text-2xl font-semibold text-charcoal">
                        ${((status?.balances?.available || 0) / 100).toFixed(2)}
                      </p>
                    </div>
                    <div className="bg-white p-4 rounded border border-surface-border">
                      <p className="text-sm text-charcoal-muted">Pending Volume</p>
                      <p className="text-2xl font-semibold text-charcoal">
                        ${((status?.balances?.pending || 0) / 100).toFixed(2)}
                      </p>
                    </div>
                  </div>

                  <div className="bg-white p-4 rounded border border-surface-border flex justify-between items-center flex-wrap gap-4">
                    <div>
                      <p className="text-sm text-charcoal-muted mb-1">Payout Schedule</p>
                      <p className="font-medium text-charcoal capitalize">
                        {status?.payoutSchedule?.interval || 'Daily'}
                        {status?.payoutSchedule?.delay_days ? ` (${status.payoutSchedule.delay_days} day delay)` : ''}
                      </p>
                    </div>
                    <Button
                      onClick={handleInstantPayout}
                      disabled={payoutLoading || !status?.balances?.available}
                      className="bg-gold text-black hover:bg-gold/90 min-w-[140px]"
                    >
                      {payoutLoading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Instant Payout'}
                    </Button>
                  </div>
                </div>

                <div className="space-y-3">
                  <p className="text-green-400/90 text-sm">
                    Your Stripe account is fully set up. You can receive payouts from donations.
                  </p>
                  <Button
                    onClick={handleEditDetails}
                    disabled={editLoading}
                    variant="outline"
                    className="border-gold/40 text-charcoal hover:bg-gold/10 flex items-center gap-2"
                  >
                    {editLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Opening...
                      </>
                    ) : (
                      <>
                        <Pencil className="w-4 h-4" />
                        Edit details
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
